import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ArrowRight, CheckCircle2, ChevronLeft, ChevronRight, Info, OctagonAlert, X } from "lucide-react";
import { useAppServices } from "../hook/services";
import { cx } from "../utils";

/**
 * The notification bar at the top of an app page.
 *
 * Shows the announcements the super admin has published for this app (or for
 * every app), most severe first. More than one pages with arrows rather than
 * stacking, so the bar stays one line tall. A dismissed announcement stays
 * hidden for this browser — until it's edited, since an edit is usually news.
 */

const TONES = {
  info: {
    icon: Info,
    bar: "bg-brand-50 text-brand-900 border-brand-200/70",
    iconWrap: "bg-brand-600 text-white",
    link: "bg-brand-600 text-white hover:bg-brand-700",
    muted: "text-brand-800/80",
    control: "text-brand-700 hover:bg-brand-100",
  },
  success: {
    icon: CheckCircle2,
    bar: "bg-emerald-50 text-emerald-900 border-emerald-200/70",
    iconWrap: "bg-emerald-600 text-white",
    link: "bg-emerald-600 text-white hover:bg-emerald-700",
    muted: "text-emerald-800/80",
    control: "text-emerald-700 hover:bg-emerald-100",
  },
  warning: {
    icon: AlertTriangle,
    bar: "bg-amber-50 text-amber-950 border-amber-200/80",
    iconWrap: "bg-amber-500 text-white",
    link: "bg-amber-600 text-white hover:bg-amber-700",
    muted: "text-amber-900/80",
    control: "text-amber-800 hover:bg-amber-100",
  },
  critical: {
    icon: OctagonAlert,
    bar: "bg-rose-50 text-rose-950 border-rose-200/80",
    iconWrap: "bg-rose-600 text-white",
    link: "bg-rose-600 text-white hover:bg-rose-700",
    muted: "text-rose-900/80",
    control: "text-rose-700 hover:bg-rose-100",
  },
};

// "#support" and the like are the page's own tabs — opened in place, where
// any other link opens in a new tab.
const linkProps = (url) =>
  String(url || "").startsWith("#") ? {} : { target: "_blank", rel: "noopener noreferrer" };

const STORE = "sc_dismissed_announcements";

// Storage can throw (private mode, blocked site data, sandboxed iframes) —
// the bar then simply forgets dismissals between visits.
const readDismissed = () => {
  try {
    return JSON.parse(localStorage.getItem(STORE) || "[]");
  } catch {
    return [];
  }
};
const writeDismissed = (list) => {
  try {
    localStorage.setItem(STORE, JSON.stringify(list.slice(-100)));
  } catch {
    /* ignore */
  }
};

// Keyed on the edit time too, so an updated announcement shows again.
const keyOf = (a) => `${a._id}:${a.updatedAt}`;

const AnnouncementBar = ({ appId, className }) => {
  const { announcements } = useAppServices();
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!appId) return undefined;
    let alive = true;
    (async () => {
      const { response } = await announcements.Active(appId);
      if (alive) setItems(Array.isArray(response) ? response : []);
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  const visible = useMemo(() => items.filter((a) => !dismissed.includes(keyOf(a))), [items, dismissed]);

  useEffect(() => {
    if (index > visible.length - 1) setIndex(Math.max(0, visible.length - 1));
  }, [visible.length, index]);

  if (visible.length === 0) return null;

  const a = visible[Math.min(index, visible.length - 1)];
  const t = TONES[a.tone] || TONES.info;
  const Icon = t.icon;

  const dismiss = () => {
    const next = [...dismissed, keyOf(a)];
    setDismissed(next);
    writeDismissed(next);
  };

  return (
    <div
      role={a.tone === "critical" || a.tone === "warning" ? "alert" : "status"}
      className={cx("animate-fade-in rounded-xl border px-3 py-2.5 shadow-sm", t.bar, className)}
    >
      <div className="flex items-start gap-3 sm:items-center">
        <span className={cx("grid h-7 w-7 shrink-0 place-items-center rounded-lg", t.iconWrap)} aria-hidden>
          <Icon size={15} />
        </span>

        <p className="min-w-0 flex-1 text-[13px] leading-snug">
          <span className="font-semibold">{a.title}</span>
          {a.message && <span className={cx("ml-1.5", t.muted)}>{a.message}</span>}
        </p>

        <div className="flex shrink-0 items-center gap-1">
          {a.linkUrl && (
            <a
              href={a.linkUrl}
              {...linkProps(a.linkUrl)}
              className={cx("hidden h-7 items-center gap-1 rounded-lg px-2.5 text-[12px] font-semibold transition-colors sm:inline-flex", t.link)}
            >
              {a.linkLabel || "Learn more"} <ArrowRight size={12} />
            </a>
          )}

          {visible.length > 1 && (
            <span className="ml-1 flex items-center">
              <button
                type="button"
                onClick={() => setIndex((i) => (i - 1 + visible.length) % visible.length)}
                className={cx("grid h-7 w-7 place-items-center rounded-lg transition-colors", t.control)}
                aria-label="Previous announcement"
              >
                <ChevronLeft size={15} />
              </button>
              <span className={cx("min-w-[2.5rem] text-center text-[11.5px] font-medium tabular-nums", t.muted)}>
                {index + 1} / {visible.length}
              </span>
              <button
                type="button"
                onClick={() => setIndex((i) => (i + 1) % visible.length)}
                className={cx("grid h-7 w-7 place-items-center rounded-lg transition-colors", t.control)}
                aria-label="Next announcement"
              >
                <ChevronRight size={15} />
              </button>
            </span>
          )}

          {a.dismissible && (
            <button
              type="button"
              onClick={dismiss}
              className={cx("grid h-7 w-7 place-items-center rounded-lg transition-colors", t.control)}
              aria-label="Dismiss announcement"
            >
              <X size={15} />
            </button>
          )}
        </div>
      </div>

      {/* The button moves under the text on narrow screens rather than squeezing it. */}
      {a.linkUrl && (
        <a
          href={a.linkUrl}
          {...linkProps(a.linkUrl)}
          className={cx("ml-10 mt-2 inline-flex h-7 items-center gap-1 rounded-lg px-2.5 text-[12px] font-semibold sm:hidden", t.link)}
        >
          {a.linkLabel || "Learn more"} <ArrowRight size={12} />
        </a>
      )}
    </div>
  );
};

export default AnnouncementBar;
